import React from 'react';
import {
    Form,
    Space,
    Radio,
    DatePicker,
    Input,
    InputNumber,
    Modal,
    message,
    Checkbox,
    Select,
    AutoComplete,
    Button,
} from 'antd';
import { EProspectType, Meta, Prospect, TCarrier, TProspectInsurance, MaritalStatusValue } from 'types/common';
import { createProspect, updateProspect } from 'api/prospect';
import { gpUpdateProspect } from 'api/group';
import { getProviderPrivateFileSigned } from 'api/common';
import { getPrivateFilePreview } from 'api/operation';
import { isOpPortal, findTargetMetaFromListByKey, formatDate } from 'utils/common';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import StateSelect from 'components/form/StateSelect';
import { FormProspect, FormInsurance } from './types';
import dayjs from 'dayjs';
// import useMyListedChannels from 'hooks/useMyListedChannels';
import MyChannelSelect from 'components/form/MyChannelSelect';
import { REQUIRED_FIELD } from 'constants/common';
import UsPhoneInput from 'components/form/USPhoneInput';
import FormValidationHelper from 'utils/validation';
import useInsurance from 'hooks/useInsurance';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import FileUploadProspect from 'components/form/FileUploadProspect';
import FileUploadPrivate from 'components/form/FileUploadPrivate';
import useProviderServiceFlag from 'hooks/useProviderServiceFlag';
import BirthdayInput from 'components/form/BirthdayInput';
import AutoAddressInput from 'components/AutoAddressInput';
import { sortByProperty } from 'utils/form';
import { defaultIndexOf } from 'utils/clientEligibility';

const InputTextArea = Input.TextArea;

type Props = {
    show: boolean;
    item?: Prospect;
    onCannel?: (needRefresh?: boolean) => void;
    onSuccess?: (id?: number) => void;
    wrapClassName?: string;
    type?: 1 | 2; // 1 basic info, 2 insurance info
    onClickEmptyState?: () => void;
    isGroupPractice?: boolean;
};

enum EBillingType {
    SELF_PAY = 'Self-pay',
    INSURANCE = 'Insurance',
}

const BillingTypeOption = [
    {
        label: EBillingType.SELF_PAY,
        value: EBillingType.SELF_PAY,
    },
    {
        label: EBillingType.INSURANCE,
        value: EBillingType.INSURANCE,
    },
];

const IN_APP_CHAT = 'In-app chat';
const TEXT_MESSAGE = 'Text message';
const PHONE_CALL = 'Phone call';
const EMAIL = 'Email';

const RELATIONSHIP_OPTIONS = [
    { label: 'Spouse / Partner', value: 'spouse' },
    { label: 'Parent', value: 'parent' },
    { label: 'Child', value: 'child' },
    { label: 'Sibling', value: 'sibling' },
    { label: 'Friend', value: 'friend' },
    { label: 'Caregiver', value: 'caregiver' },
    { label: 'Other', value: 'other' },
];

const POLICY_HOLDER_RELATIONSHIP_OPTIONS = [
    { label: 'Child', value: 'CHILD' },
    { label: 'Parent', value: 'PARENT' },
    { label: 'Spouse', value: 'SPOUSE' },
    { label: 'Other', value: 'OTHER' },
];

const AddAndUpdateProspects = (props: Props) => {
    const { show, item, onCannel, onSuccess, wrapClassName, type, onClickEmptyState, isGroupPractice } = props;
    const [submiting, setSubmiting] = React.useState(false);
    const [form] = Form.useForm<FormProspect>();
    const [isOthers, setIsOthers] = React.useState(false);
    const [setting, loading] = useSettings();
    const stateOption = sortByProperty(setting[EnumFields.STATE] || [], 'label');
    const stateOptionRef = React.useRef<any>();
    const [flag] = useProviderServiceFlag();
    const isKlarity = !!flag?.klarityServiceFlag;

    const isEdit = !!item?.id;
    const [currentBillingType, setCurrentBillingType] = React.useState<EBillingType>();
    const [selfPayChecked, setSelfPayChecked] = React.useState(false);
    const [insuranceChecked, setInsuranceChecked] = React.useState(false);
    const createEmptyInsurance = (insuranceType: 'PRIMARY' | 'SECONDARY'): FormInsurance => ({
        insuranceType,
        policyHolderType: 'SELF',
        carrier: '',
        plan: '',
        memberId: '',
        groupId: '',
        front: null,
        backend: null,
    });

    const [primaryInsurances, setPrimaryInsurances] = React.useState<FormInsurance[]>([
        createEmptyInsurance('PRIMARY'),
    ]);
    const [secondaryInsurances, setSecondaryInsurances] = React.useState<FormInsurance[]>([]);
    const [activeInsuranceTab, setActiveInsuranceTab] = React.useState<'PRIMARY' | 'SECONDARY'>('PRIMARY');

    const [targetState, setTargetState] = React.useState<Meta>();

    const [allInsurance, allInsuranceLoading, fetchAllInsurance] = useInsurance();
    const [showEmptyStateError, setShowEmptyStateError] = React.useState(false);

    const [preferMethod, setPreferMethod] = React.useState<string[]>([TEXT_MESSAGE, PHONE_CALL, EMAIL]);
    const [idFrontPreviewUrl, setIdFrontPreviewUrl] = React.useState<string | undefined>(undefined);
    const [idBackPreviewUrl, setIdBackPreviewUrl] = React.useState<string | undefined>(undefined);

    const stateCarrier = targetState
        ? allInsurance.find((ins) => ins.state === targetState.label || ins.state === targetState.value)
        : undefined;

    const carrierOptions: TCarrier[] = stateCarrier?.carriers || [];

    const getPlanOptions = (carrierName: string): string[] => {
        if (!carrierName) return [];
        return stateCarrier?.carriers.find((c) => c.name === carrierName)?.plans || [];
    };

    const updateInsuranceItem = (
        list: FormInsurance[],
        setList: React.Dispatch<React.SetStateAction<FormInsurance[]>>,
        index: number,
        field: Partial<FormInsurance>,
    ) => {
        const updated = [...list];
        updated[index] = { ...updated[index], ...field };
        setList(updated);
    };

    const options = [
        { label: IN_APP_CHAT, value: IN_APP_CHAT, key: IN_APP_CHAT },
        { label: TEXT_MESSAGE, value: TEXT_MESSAGE, key: TEXT_MESSAGE },
        { label: PHONE_CALL, value: PHONE_CALL, key: PHONE_CALL },
        { label: EMAIL, value: EMAIL, key: EMAIL },
    ];

    const scrollToState = () => {
        const target = document.getElementById('state-input');
        try {
            target?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        } catch (e) {
            console.error(e);
        }
    };

    const showBasicInfo = !item || !type || (item && type === 1);
    const showPaymentInfo = !item || !type || (item && type === 2);

    React.useEffect(() => {
        if (insuranceChecked && primaryInsurances.length > 0) {
            const firstName = form.getFieldValue('firstName');
            const lastName = form.getFieldValue('lastName');
            const first = primaryInsurances[0];
            if (!first.firstName && firstName) {
                updateInsuranceItem(primaryInsurances, setPrimaryInsurances, 0, { firstName });
            }
            if (!first.lastName && lastName) {
                updateInsuranceItem(primaryInsurances, setPrimaryInsurances, 0, { lastName });
            }
        }
    }, [insuranceChecked]);

    // const [myChannels] = useMyListedChannels();
    const onSubmit = async () => {
        try {
            const createTime = formatDate(dayjs());

            // basic 区块隐藏时仍挂载（height: 0），其字段报错用户看不到，只校验付款区块的字段
            await form.validateFields(
                showBasicInfo ? undefined : ['selfPay', 'cardNum', 'cardExpirationDate', 'cardCvc', 'insurance'],
            );
            const formValues: FormProspect = form.getFieldsValue(true);
            if (showBasicInfo && !formValues.tel && !formValues.email) {
                message.error('Please enter an email or phone number');
                return;
            }
            if (showPaymentInfo && insuranceChecked) {
                const allInsuranceItems = [...primaryInsurances, ...secondaryInsurances];
                for (const ins of allInsuranceItems) {
                    if (ins.policyHolderType === 'SELF') {
                        if (!ins.firstName) {
                            message.error('Member legal first name is required');
                            return;
                        }
                        if (!ins.lastName) {
                            message.error('Member legal last name is required');
                            return;
                        }
                    }
                    if (!ins.carrier) {
                        message.error('Insurance carrier is required');
                        return;
                    }
                    if (!ins.memberId) {
                        message.error('Member ID is required');
                        return;
                    }
                    if (ins.policyHolderType === 'SOMEONE_ELSE') {
                        if (!ins.relationship) {
                            message.error('Relationship is required');
                            return;
                        }
                        if (!ins.holderFirstName || !ins.holderLastName) {
                            message.error('Policy holder name is required');
                            return;
                        }
                        if (!ins.holderDob) {
                            message.error('Policy holder date of birth is required');
                            return;
                        }
                        if (!ins.holderGender) {
                            message.error('Policy holder gender is required');
                            return;
                        }
                    }
                }
            }
            let billing = '';
            if (selfPayChecked && insuranceChecked) {
                billing = `${EBillingType.SELF_PAY},${EBillingType.INSURANCE}`;
            } else if (selfPayChecked) {
                billing = EBillingType.SELF_PAY;
            } else if (insuranceChecked) {
                billing = EBillingType.INSURANCE;
            }
            const data: Prospect = {
                preferMethod: formValues.preferMethod!.join(','),
                birthday: formValues.birthday
                    ? dayjs(formValues.birthday, 'MM/DD/YYYY').format('YYYY-MM-DD')
                    : undefined,
                type: item?.type || EProspectType.PATIENT,
                address: formValues.address,
                billing,
                city: formValues.city,
                email: formValues.email,
                firstName: formValues.firstName,
                gender: formValues.gender,
                lastName: formValues.lastName,
                notes: formValues.notes,
                state: formValues.state,
                tel: formValues.tel,
                zip: formValues.zip,
                idFront: formValues.idFront,
                idBack: formValues.idBack,
                sendInvitationEmail: formValues.sendInvitationEmail,
                maritalStatus: formValues.maritalStatus,
            };

            // Add emergency contact if provided
            if (formValues.emergencyContactFullName || formValues.emergencyContactPhone) {
                data.emergencyContacts = [
                    {
                        id: item?.emergencyContacts?.[0]?.id || 0,
                        fullName: formValues.emergencyContactFullName || '',
                        phone: formValues.emergencyContactPhone || '',
                        relationship: formValues.emergencyContactRelationship || '',
                        priority: 1,
                    },
                ];
            }

            if (selfPayChecked) {
                data.cardNum = formValues.cardNum;
                data.cardExpirationDate = formValues.cardExpirationDate?.format('MM/YY');
                data.cardCvc = formValues.cardCvc;
            }

            if (insuranceChecked) {
                data.insurances = [];
                let sort = 1;
                const buildInsuranceList = (list: FormInsurance[], type: 'PRIMARY' | 'SECONDARY') => {
                    const defaultIdx = defaultIndexOf(list);
                    list.forEach((ins, idx) => {
                        const insData: TProspectInsurance = {
                            id: ins.id,
                            carrier: ins.carrier,
                            plan: ins.plan,
                            memberId: ins.memberId,
                            groupId: ins.groupId,
                            front: ins.front,
                            backend: ins.backend,
                            sort: sort++,
                            firstName: ins.firstName,
                            lastName: ins.lastName,
                            insuranceType: type,
                            isDefault: idx === defaultIdx,
                            policyHolderType: ins.policyHolderType,
                        };
                        if (ins.policyHolderType === 'SOMEONE_ELSE') {
                            insData.relationship = ins.relationship;
                            insData.holderFirstName = ins.holderFirstName;
                            insData.holderLastName = ins.holderLastName;
                            insData.holderDob = ins.holderDob
                                ? dayjs(ins.holderDob, 'MM/DD/YYYY').format('YYYY-MM-DD')
                                : undefined;
                            insData.holderGender = ins.holderGender;
                        }
                        if (isEdit && ins.id) {
                            const original = item?.insurances?.find((o) => o.id === ins.id);
                            if (original) {
                                if (insData.backend === original.backend) insData.backend = null;
                                if (insData.front === original.front) insData.front = null;
                            }
                        }
                        data.insurances!.push(insData);
                    });
                };
                buildInsuranceList(primaryInsurances, 'PRIMARY');
                buildInsuranceList(secondaryInsurances, 'SECONDARY');
            }

            setSubmiting(true);
            if (isEdit) {
                data.id = item.id;
                data.sendInvitationEmail = item.sendInvitationEmail;
                if (item.idBack === data.idBack) {
                    data.idBack = null;
                }
                if (item.idFront === data.idFront) {
                    data.idFront = null;
                }
                const result =
                    isGroupPractice && item.identityId
                        ? await gpUpdateProspect(item.identityId, data)
                        : await updateProspect(item.id!, data, isGroupPractice ? item.providerId : undefined);
                if (result.error) {
                    message.error(result.error);
                } else {
                    message.success('Patient information has been updated successfully');
                    if (onSuccess) {
                        onSuccess();
                    }
                }
            } else {
                data.createTime = createTime;
                const result = await createProspect(data, isGroupPractice ? item?.providerId : undefined);
                if (result.error) {
                    //message.error(result.error);
                } else {
                    const id = result.data.data;
                    message.success("You've successfully added a patient");
                    if (onSuccess) {
                        onSuccess(id);
                    }
                }
            }
        } catch (e: any) {
            console.error(e);
            if (e.errorFields) {
                //form error
                return;
            }
            //const msg = e?.toString?.() || 'data error';
            //message.error(msg);
        } finally {
            setSubmiting(false);
        }
    };

    // 获取 ID 图片预览 URL 的独立 effect
    React.useEffect(() => {
        if (show && item?.idFront) {
            const fetchIdCardUrls = async () => {
                const isOps = isOpPortal();
                const fetchUrl = async (resourceKey: string | null | undefined) => {
                    if (resourceKey?.includes('http')) {
                        return resourceKey;
                    }
                    if (!resourceKey) return undefined;
                    try {
                        const res = isOps
                            ? await getPrivateFilePreview(resourceKey)
                            : await getProviderPrivateFileSigned(resourceKey);
                        const signedUrl = res.data?.data?.signedUrl || res.data?.data?.singedUrl;
                        if (!res.error && signedUrl) {
                            return signedUrl;
                        }
                    } catch (error) {
                        // eslint-disable-next-line no-console
                        console.error(`Failed to fetch image URL for resourceKey: ${resourceKey}`, error);
                    }
                    return undefined;
                };

                const frontUrl = await fetchUrl(item.idFront);
                const backUrl = await fetchUrl(item.idBack);
                setIdFrontPreviewUrl(frontUrl);
                setIdBackPreviewUrl(backUrl);
            };

            fetchIdCardUrls();
        } else if (!show) {
            // 弹窗关闭时清空预览 URL
            setIdFrontPreviewUrl(undefined);
            setIdBackPreviewUrl(undefined);
        }
    }, [show, item?.idFront, item?.idBack]);

    React.useEffect(() => {
        stateOptionRef.current = stateOption;
        if (item && stateOption && allInsurance && stateOption.length > 0 && allInsurance.length > 0) {
            const formVal: FormProspect = {
                firstName: item.firstName,
                lastName: item.lastName,
                email: item.email,
                //@ts-ignore
                birthday: item.birthday ? dayjs(item.birthday, 'YYYY-MM-DD').format('MM/DD/YYYY') : undefined,
                tel: item.tel,
                address: item.address,
                city: item.city,
                state: item.state,
                zip: item.zip,
                gender: item.gender ? (['male', 'female'].includes(item.gender) ? item.gender : 'other') : undefined,
                notes: item.notes,
                idBack: item.idBack!,
                idFront: item.idFront!,
                //@ts-ignore
                preferMethod: item.preferMethod?.split(',') || [],
                sendInvitationEmail: item.sendInvitationEmail,
                maritalStatus: item.maritalStatus,
                emergencyContactFullName: item.emergencyContacts?.[0]?.fullName,
                emergencyContactPhone: item.emergencyContacts?.[0]?.phone,
                emergencyContactRelationship: item.emergencyContacts?.[0]?.relationship,
            };

            if (item.billing?.includes(EBillingType.SELF_PAY)) {
                setSelfPayChecked(true);
                formVal.cardNum = item.cardNum || item.patientCardNum || '';
                const _cardExpirationDate = item.cardExpirationDate || item.patientCardExpirationDate;
                formVal.cardExpirationDate = _cardExpirationDate ? dayjs(_cardExpirationDate, 'MM/YY') : undefined;
                formVal.cardCvc = item.cardCvc || item.patientCardCvc || '';
            }

            if (item.state) {
                const st = stateOption?.find((i) => {
                    return i.value === item.state || i.label === item.state;
                });
                setTargetState(st);
            }
            if (item.insurances && item.insurances.length > 0) {
                setInsuranceChecked(true);
                const sorted = [...item.insurances].sort((a, b) => (a.sort || 0) - (b.sort || 0));
                const primaries: FormInsurance[] = [];
                const secondaries: FormInsurance[] = [];
                sorted.forEach((ins) => {
                    const formIns: FormInsurance = {
                        id: ins.id,
                        insuranceType: ins.insuranceType || (ins.sort === 1 ? 'PRIMARY' : 'SECONDARY'),
                        isDefault: ins.isDefault,
                        policyHolderType: ins.policyHolderType || 'SELF',
                        relationship: ins.relationship,
                        holderFirstName: ins.holderFirstName,
                        holderLastName: ins.holderLastName,
                        holderDob:
                            ins.holderDob && ins.holderDob.includes('-')
                                ? dayjs(ins.holderDob, 'YYYY-MM-DD').format('MM/DD/YYYY')
                                : ins.holderDob,
                        holderGender: ins.holderGender
                            ? ['male', 'female'].includes(ins.holderGender)
                                ? ins.holderGender
                                : 'other'
                            : undefined,
                        firstName: ins.firstName,
                        lastName: ins.lastName,
                        carrier: ins.carrier,
                        plan: ins.plan,
                        memberId: ins.memberId,
                        groupId: ins.groupId,
                        front: ins.front,
                        backend: ins.backend,
                    };
                    if (formIns.insuranceType === 'PRIMARY') {
                        primaries.push(formIns);
                    } else {
                        secondaries.push(formIns);
                    }
                });
                setPrimaryInsurances(primaries.length > 0 ? primaries : [createEmptyInsurance('PRIMARY')]);
                setSecondaryInsurances(secondaries);
            }
            form.setFieldsValue(formVal);
        }
    }, [item, stateOption, allInsurance]);

    React.useEffect(() => {
        if (show && !item) {
            setPrimaryInsurances([createEmptyInsurance('PRIMARY')]);
            setSecondaryInsurances([]);
            setActiveInsuranceTab('PRIMARY');
        }
    }, [show, item]);

    const addInsurance = (insType: 'PRIMARY' | 'SECONDARY') => {
        if (insType === 'PRIMARY') {
            setPrimaryInsurances((prev) => [...prev, createEmptyInsurance('PRIMARY')]);
        } else {
            setSecondaryInsurances((prev) => [...prev, createEmptyInsurance('SECONDARY')]);
        }
    };

    const removeInsurance = (insType: 'PRIMARY' | 'SECONDARY', index: number) => {
        if (insType === 'PRIMARY') {
            setPrimaryInsurances((prev) => prev.filter((_, i) => i !== index));
        } else {
            setSecondaryInsurances((prev) => prev.filter((_, i) => i !== index));
        }
    };

    const renderDeleteIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
                d="M19 7L18.1327 19.1425C18.0579 20.1891 17.187 21 16.1378 21H7.86224C6.81296 21 5.94208 20.1891 5.86732 19.1425L5 7M10 11V17M14 11V17M15 7V4C15 3.44772 14.5523 3 14 3H10C9.44772 3 9 3.44772 9 4V7M4 7H20"
                stroke="#6B7280"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );

    const renderInsuranceCard = (ins: FormInsurance, index: number, insType: 'PRIMARY' | 'SECONDARY') => {
        const list = insType === 'PRIMARY' ? primaryInsurances : secondaryInsurances;
        const setList = insType === 'PRIMARY' ? setPrimaryInsurances : setSecondaryInsurances;
        const canRemove = insType === 'PRIMARY' ? primaryInsurances.length > 1 : true;
        const update = (field: Partial<FormInsurance>) => updateInsuranceItem(list, setList, index, field);

        return (
            <div key={`${insType}-${index}`}>
                {index > 0 && <div className={s.insuranceCardDivider} />}
                <div className={s.cardHeaderRow}>
                    <div className={s.rowItem}>
                        <Form.Item style={{ width: '500px' }} label="Primary policy holder" required>
                            <Radio.Group
                                value={ins.policyHolderType}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    update({
                                        policyHolderType: val,
                                        ...(val === 'SELF'
                                            ? {
                                                  relationship: undefined,
                                                  holderFirstName: undefined,
                                                  holderLastName: undefined,
                                                  holderDob: undefined,
                                                  holderGender: undefined,
                                              }
                                            : {}),
                                    });
                                }}
                            >
                                <Radio value="SELF">Client</Radio>
                                <Radio value="SOMEONE_ELSE">Someone else</Radio>
                            </Radio.Group>
                        </Form.Item>
                    </div>
                    {canRemove && (
                        <span className={s.removeBtn} onClick={() => removeInsurance(insType, index)}>
                            {renderDeleteIcon()}
                        </span>
                    )}
                </div>
                {ins.policyHolderType === 'SOMEONE_ELSE' && (
                    <div className={s.someoneElseBox}>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '300px' }}
                                    label="Client's relationship to the policy holder"
                                    required
                                >
                                    <Select
                                        value={ins.relationship}
                                        placeholder="Please select"
                                        onChange={(val) => update({ relationship: val })}
                                        options={POLICY_HOLDER_RELATIONSHIP_OPTIONS}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        {ins.relationship && (
                            <>
                                <div className={s.row}>
                                    <div className={s.rowItem}>
                                        <Form.Item style={{ width: '300px' }} label="Legal first name" required>
                                            <Input
                                                maxLength={25}
                                                value={ins.holderFirstName}
                                                onChange={(e) => update({ holderFirstName: e.target.value })}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div className={s.rowItem}>
                                        <Form.Item style={{ width: '300px' }} label="Legal last name" required>
                                            <Input
                                                maxLength={25}
                                                value={ins.holderLastName}
                                                onChange={(e) => update({ holderLastName: e.target.value })}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>
                                <div className={s.row}>
                                    <div className={s.rowItem}>
                                        <Form.Item style={{ width: '300px' }} label="Date of birth" required>
                                            <BirthdayInput
                                                value={ins.holderDob}
                                                onChange={(val) => update({ holderDob: val as string })}
                                            />
                                        </Form.Item>
                                    </div>
                                    <div className={s.rowItem}>
                                        <Form.Item style={{ width: '300px' }} label="Member gender" required>
                                            <Select
                                                value={ins.holderGender}
                                                onChange={(val) => update({ holderGender: val })}
                                                options={[
                                                    { label: 'Male', value: 'male' },
                                                    { label: 'Female', value: 'female' },
                                                    { label: 'Others', value: 'other' },
                                                ]}
                                            />
                                        </Form.Item>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}
                {ins.policyHolderType === 'SELF' && (
                    <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item style={{ width: '300px' }} label="Member legal first name" required>
                                <Input
                                    maxLength={25}
                                    value={ins.firstName}
                                    onChange={(e) => update({ firstName: e.target.value })}
                                />
                            </Form.Item>
                        </div>
                        <div className={s.rowItem}>
                            <Form.Item style={{ width: '300px' }} label="Member legal last name" required>
                                <Input
                                    maxLength={25}
                                    value={ins.lastName}
                                    onChange={(e) => update({ lastName: e.target.value })}
                                />
                            </Form.Item>
                        </div>
                    </div>
                )}
                <div className={s.row}>
                    <div className={s.rowItem}>
                        <Form.Item style={{ width: '300px' }} label="Insurance carrier" required>
                            <AutoComplete
                                style={{ width: '100%' }}
                                options={carrierOptions.map((c) => ({ value: c.name }))}
                                value={ins.carrier}
                                placeholder="Please select or input"
                                filterOption={(inputValue, option) =>
                                    !!option?.value.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                onChange={(val) => update({ carrier: val, plan: '' })}
                            >
                                <Input allowClear />
                            </AutoComplete>
                        </Form.Item>
                    </div>
                    <div className={s.rowItem}>
                        <Form.Item style={{ width: '300px' }} label="Insurance plan">
                            <AutoComplete
                                style={{ width: '100%' }}
                                options={getPlanOptions(ins.carrier).map((p) => ({ value: p }))}
                                value={ins.plan}
                                placeholder="Please select or input"
                                filterOption={(inputValue, option) =>
                                    !!option?.value.toLowerCase().includes(inputValue.toLowerCase())
                                }
                                onChange={(val) => update({ plan: val })}
                            >
                                <Input allowClear />
                            </AutoComplete>
                        </Form.Item>
                    </div>
                </div>
                <div className={s.row}>
                    <div className={s.rowItem}>
                        <Form.Item style={{ width: '300px' }} label="Member ID" required>
                            <Input
                                value={ins.memberId}
                                onChange={(e) => update({ memberId: e.target.value })}
                                allowClear
                            />
                        </Form.Item>
                    </div>
                    <div className={s.rowItem}>
                        <Form.Item style={{ width: '300px' }} label="Group ID">
                            <Input
                                value={ins.groupId}
                                onChange={(e) => update({ groupId: e.target.value })}
                                allowClear
                            />
                        </Form.Item>
                    </div>
                </div>
                <div className={s.row}>
                    <div className={s.rowItem}>
                        <Form.Item style={{ width: '260px' }} label="">
                            <FileUploadPrivate
                                accept="image/*"
                                uploadType="PROSPECT_INSURANCE"
                                notNeedSign
                                value={ins.front}
                                onChange={(val) => update({ front: val as string | null })}
                            />
                        </Form.Item>
                    </div>
                    <div className={s.rowItem}>
                        <Form.Item style={{ width: '260px' }} label="">
                            <FileUploadPrivate
                                accept="image/*"
                                uploadType="PROSPECT_INSURANCE"
                                notNeedSign
                                value={ins.backend}
                                onChange={(val) => update({ backend: val as string | null })}
                            />
                        </Form.Item>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            title={isEdit ? 'Edit the client' : 'Create new client'}
            closable
            className={commonS.modalFixHeightWrap}
            width={680}
            onCancel={() => {
                onCannel?.();
            }}
            okText="Save"
            open={show}
            onOk={onSubmit}
            destroyOnClose
            okButtonProps={{ loading: submiting }}
            zIndex={99999}
            wrapClassName={wrapClassName || ''}
        >
            <Form
                form={form}
                name="basic"
                className={commonS.formStyle1}
                initialValues={{
                    remember: true,
                    preferMethod,
                    sendInvitationEmail: true,
                }}
                autoComplete="off"
                layout="vertical"
                scrollToFirstError
            >
                <div className={s.modalWrap}>
                    <div className={`${s.basicWrap} ${showBasicInfo ? s.basicWrapShow : ''}`}>
                        <h3 className={s.basicInfoTitle}>Basic information</h3>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '300px' }}
                                    label="First name"
                                    name={['firstName']}
                                    rules={[{ required: true, message: REQUIRED_FIELD }]}
                                >
                                    <Input maxLength={25} placeholder="" />
                                </Form.Item>
                            </div>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '300px' }}
                                    label="Last name"
                                    name={['lastName']}
                                    rules={[{ required: true, message: REQUIRED_FIELD }]}
                                >
                                    <Input max={25} placeholder="" />
                                </Form.Item>
                            </div>
                        </div>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '300px' }}
                                    label="Phone number"
                                    name={['tel']}
                                    validateFirst
                                    rules={[
                                        { required: true, message: REQUIRED_FIELD },
                                        FormValidationHelper.validateUSPhoneNumber(
                                            'Please enter a 10-digit phone number',
                                        ),
                                    ]}
                                >
                                    <UsPhoneInput />
                                </Form.Item>
                            </div>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '300px' }}
                                    label="Email"
                                    name={['email']}
                                    validateFirst
                                    rules={[{ type: 'email', message: REQUIRED_FIELD }]}
                                >
                                    <Input />
                                </Form.Item>
                            </div>
                        </div>
                        {/* <div className={s.row}>
                        <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '360px' }}
                                label="Select a channel:"
                                name={['channelId']}
                                validateFirst
                                rules={[{ required: true, message: 'Channel is required' }]}
                            >
                                <MyChannelSelect showOthers />
                            </Form.Item>
                        </div>
                    </div> */}
                        <h3 className={s.h3Btm}>Personal Information</h3>
                        <div className={s.row}>
                            {/* <div className={s.rowItem}>
                            <Form.Item
                                style={{ width: '300px' }}
                                label="Date of birth"
                                name={['birthday']}
                                rules={[{ required: false, message: 'Date of birth is required' }]}
                            >
                                <DatePicker
                                    defaultPickerValue={
                                        item?.birthday ? undefined : dayjs().add(-18, 'years')
                                    }
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </div> */}
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '300px' }}
                                    label="Date of birth"
                                    name={['birthday']}
                                    rules={[
                                        {
                                            required: true,
                                            message: REQUIRED_FIELD,
                                        },
                                        FormValidationHelper.validateBirthday('Invalid date format'),
                                    ]}
                                >
                                    <BirthdayInput style={{ width: '100%' }} />
                                </Form.Item>
                            </div>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '300px' }}
                                    label="Gender"
                                    name={['gender']}
                                    rules={[{ required: true, message: REQUIRED_FIELD }]}
                                >
                                    <Select
                                        options={[
                                            { label: 'Male', value: 'male' },
                                            { label: 'Female', value: 'female' },
                                            { label: 'Others', value: 'other' },
                                        ]}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item style={{ width: '315px' }} label="Marital status" name={['maritalStatus']}>
                                    <Select placeholder="Select marital status">
                                        {Object.values(MaritalStatusValue).map((status) => {
                                            // Convert to lowercase, replace underscores with spaces, then capitalize only first letter
                                            const text = status.toLowerCase().replace(/_/g, ' ');
                                            const displayText = text.charAt(0).toUpperCase() + text.slice(1);
                                            return (
                                                <Select.Option key={status} value={status}>
                                                    {displayText}
                                                </Select.Option>
                                            );
                                        })}
                                    </Select>
                                </Form.Item>
                            </div>
                        </div>
                        <h4>ID</h4>
                        <div className={s.subTitle}>Upload photos of your ID</div>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '260px' }}
                                    label=""
                                    name={['idFront']}
                                    rules={[{ required: false, message: 'Id photo is required' }]}
                                >
                                    <FileUploadPrivate
                                        accept="image/*"
                                        uploadType="PROSPECT_IDENTITY"
                                        notNeedSign
                                        previewUrl={idFrontPreviewUrl}
                                        previewMode="vertical"
                                    />
                                </Form.Item>
                            </div>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '260px' }}
                                    label=""
                                    name={['idBack']}
                                    rules={[{ required: false, message: 'Id photo is required' }]}
                                >
                                    <FileUploadPrivate
                                        accept="image/*"
                                        uploadType="PROSPECT_IDENTITY"
                                        notNeedSign
                                        previewUrl={idBackPreviewUrl}
                                        previewMode="vertical"
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        <h3 className={s.h3Btm}>Contact information</h3>
                        <div className={s.row}>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '360px' }}
                                    label="Address"
                                    name="address"
                                    rules={[{ required: true, message: 'Address is required' }]}
                                >
                                    <AutoAddressInput
                                        onStateChange={(val) => {
                                            const st = stateOptionRef.current?.find((item: any) => {
                                                return item.value === val || item.label === val;
                                            });
                                            if (st) {
                                                setTargetState(st);
                                                setShowEmptyStateError(false);
                                                form.setFieldValue('state', st.value);
                                            }
                                        }}
                                        onCityChange={(val) => {
                                            form.setFieldValue('city', val);
                                        }}
                                        onPostcodeChange={(val) => {
                                            form.setFieldValue('zip', val);
                                        }}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        <div className={`${s.row} ${s.rowWithGapNoWrap}`}>
                            <div style={{ width: '186px' }}>
                                <Form.Item
                                    label="City"
                                    name="city"
                                    rules={[{ required: true, message: 'City is required' }]}
                                >
                                    <Input />
                                </Form.Item>
                            </div>
                            <div id="state-input" style={{ width: '120px' }}>
                                <Form.Item
                                    label="State"
                                    name="state"
                                    rules={[{ required: true, message: 'State is required' }]}
                                >
                                    <StateSelect
                                        status={showEmptyStateError ? 'error' : undefined}
                                        onChange={(val) => {
                                            const st = stateOption?.find((item) => {
                                                return item.value === val || item.label === val;
                                            });
                                            setTargetState(st);
                                        }}
                                    />
                                </Form.Item>
                            </div>
                            <div className={s.flexItemFixed}>
                                <Form.Item
                                    label="ZIP code"
                                    name="zip"
                                    rules={[{ required: true, message: 'ZIP code is required' }]}
                                >
                                    <Input maxLength={9} />
                                </Form.Item>
                            </div>
                        </div>
                        <div className={s.row}>
                            <div className={s.rowItem} style={{ width: '100%' }}>
                                <Form.Item label="Preferred methods of contact" name={['preferMethod']}>
                                    <Checkbox.Group
                                        options={options.map((op) => {
                                            return {
                                                ...op,
                                                disabled: preferMethod.includes(op.value) && preferMethod.length === 1,
                                            };
                                        })}
                                        onChange={(e) => {
                                            setPreferMethod(e as string[]);
                                        }}
                                    />
                                </Form.Item>
                            </div>
                        </div>
                        <div className={s.row}>
                            <div className={`${s.rowItem} ${s.fullWidth}`}>
                                <h4 className={s.emergencyContactTitle}>Emergency contact</h4>
                            </div>
                        </div>
                        <div className={`${s.row} ${s.rowWithGap}`}>
                            <div className={s.flexItem}>
                                <Form.Item label="Full name" name={['emergencyContactFullName']}>
                                    <Input />
                                </Form.Item>
                            </div>
                            <div className={s.flexItem}>
                                <Form.Item label="Phone number" name={['emergencyContactPhone']}>
                                    <UsPhoneInput />
                                </Form.Item>
                            </div>
                            <div className={s.flexItem}>
                                <Form.Item label="Relationship" name={['emergencyContactRelationship']}>
                                    <Select placeholder="Select relationship">
                                        {RELATIONSHIP_OPTIONS.map((option) => (
                                            <Select.Option key={option.value} value={option.value}>
                                                {option.label}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </div>
                        </div>
                    </div>
                    {showPaymentInfo && (
                        <div className={`${s.paymentWrap} ${showPaymentInfo ? s.paymentWrapShow : ''}`}>
                            {showBasicInfo && <div className={s.line} />}
                            <div className={s.row}>
                                <div className={s.rowItem}>
                                    <Form.Item
                                        style={{ width: '360px' }}
                                        label=""
                                        fieldKey="checked"
                                        name={['selfPay']}
                                    >
                                        <Checkbox
                                            onChange={(e) => {
                                                setSelfPayChecked(e.target.checked);
                                            }}
                                            checked={selfPayChecked}
                                            className={s.checkboxLabel}
                                        >
                                            Self-pay
                                        </Checkbox>
                                    </Form.Item>
                                </div>
                            </div>
                            {selfPayChecked && (
                                <div className={s.subModule}>
                                    <div className={s.row}>
                                        <div className={s.rowItem}>
                                            <Form.Item
                                                style={{ width: '200px' }}
                                                label="Card number"
                                                name={['cardNum']}
                                                validateFirst
                                                rules={[{ required: false, message: REQUIRED_FIELD }]}
                                            >
                                                <Input />
                                            </Form.Item>
                                        </div>
                                        <div className={s.rowItem}>
                                            <Form.Item
                                                style={{ width: '140px' }}
                                                label="Expiration date"
                                                name={['cardExpirationDate']}
                                                rules={[
                                                    {
                                                        required: false,
                                                        message: REQUIRED_FIELD,
                                                    },
                                                ]}
                                            >
                                                <DatePicker format="MM/YY" style={{ width: '100%' }} />
                                            </Form.Item>
                                        </div>
                                        <div className={s.rowItem}>
                                            <Form.Item
                                                style={{ width: '112px' }}
                                                label="CVC"
                                                name={['cardCvc']}
                                                rules={[
                                                    {
                                                        required: false,
                                                        message: REQUIRED_FIELD,
                                                    },
                                                ]}
                                            >
                                                <InputNumber controls={false} type="number" step={0} />
                                            </Form.Item>
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className={s.line} />
                            <div className={s.rowItem}>
                                <Form.Item style={{ width: '360px' }} label="" fieldKey="checked" name={['insurance']}>
                                    <Checkbox
                                        checked={insuranceChecked}
                                        disabled={!targetState}
                                        onChange={(e) => {
                                            setInsuranceChecked(e.target.checked);
                                        }}
                                        className={s.checkboxLabel}
                                    >
                                        Insurance
                                    </Checkbox>
                                </Form.Item>
                            </div>
                            {!targetState && (
                                <div className={s.emptyState}>
                                    <div className={s.eLeft}>
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="20"
                                            height="20"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                clipRule="evenodd"
                                                d="M18 10C18 14.4183 14.4183 18 10 18C5.58172 18 2 14.4183 2 10C2 5.58172 5.58172 2 10 2C14.4183 2 18 5.58172 18 10ZM11 6C11 6.55228 10.5523 7 10 7C9.44772 7 9 6.55228 9 6C9 5.44772 9.44772 5 10 5C10.5523 5 11 5.44772 11 6ZM9 9C8.44772 9 8 9.44772 8 10C8 10.5523 8.44772 11 9 11V14C9 14.5523 9.44772 15 10 15H11C11.5523 15 12 14.5523 12 14C12 13.4477 11.5523 13 11 13V10C11 9.44772 10.5523 9 10 9H9Z"
                                                fill="#60A5FA"
                                            />
                                        </svg>
                                        Select the client's state first, then choose an insurance.
                                    </div>
                                    <div
                                        onClick={() => {
                                            onClickEmptyState?.();
                                            setShowEmptyStateError(true);
                                            scrollToState();
                                        }}
                                        className={s.eRight}
                                    >
                                        Select state →
                                    </div>
                                </div>
                            )}
                            {insuranceChecked && (
                                <div className={s.subModule}>
                                    <div className={s.insuranceTabs}>
                                        <div
                                            className={s.insuranceTab}
                                            style={
                                                activeInsuranceTab === 'PRIMARY'
                                                    ? { color: '#fff', background: '#006957' }
                                                    : undefined
                                            }
                                            onClick={() => setActiveInsuranceTab('PRIMARY')}
                                        >
                                            Primary
                                        </div>
                                        <div
                                            className={s.insuranceTab}
                                            style={
                                                activeInsuranceTab === 'SECONDARY'
                                                    ? { color: '#fff', background: '#006957' }
                                                    : undefined
                                            }
                                            onClick={() => setActiveInsuranceTab('SECONDARY')}
                                        >
                                            Secondary
                                        </div>
                                    </div>
                                    {activeInsuranceTab === 'PRIMARY' ? (
                                        <>
                                            {primaryInsurances.map((ins, idx) =>
                                                renderInsuranceCard(ins, idx, 'PRIMARY'),
                                            )}
                                            <div className={s.addSecond} onClick={() => addInsurance('PRIMARY')}>
                                                + Add more primary insurance
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {secondaryInsurances.length > 0 ? (
                                                <>
                                                    {secondaryInsurances.map((ins, idx) =>
                                                        renderInsuranceCard(ins, idx, 'SECONDARY'),
                                                    )}
                                                    <div
                                                        className={s.addSecond}
                                                        onClick={() => addInsurance('SECONDARY')}
                                                    >
                                                        + Add more secondary insurance
                                                    </div>
                                                </>
                                            ) : (
                                                <div className={s.emptySecondary}>
                                                    <div className={s.emptyIllustration}>
                                                        <svg
                                                            width="120"
                                                            height="80"
                                                            viewBox="0 0 120 80"
                                                            fill="none"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                        >
                                                            <rect
                                                                x="10"
                                                                y="5"
                                                                width="100"
                                                                height="70"
                                                                rx="8"
                                                                fill="#F3F4F6"
                                                                stroke="#E5E7EB"
                                                                strokeWidth="1"
                                                            />
                                                            <rect
                                                                x="22"
                                                                y="15"
                                                                width="30"
                                                                height="4"
                                                                rx="2"
                                                                fill="#D1D5DB"
                                                            />
                                                            <rect
                                                                x="68"
                                                                y="15"
                                                                width="30"
                                                                height="4"
                                                                rx="2"
                                                                fill="#D1D5DB"
                                                            />
                                                            <rect
                                                                x="22"
                                                                y="28"
                                                                width="76"
                                                                height="24"
                                                                rx="6"
                                                                fill="#E5E7EB"
                                                            />
                                                            <rect
                                                                x="28"
                                                                y="34"
                                                                width="16"
                                                                height="12"
                                                                rx="3"
                                                                fill="#3B82F6"
                                                            />
                                                            <rect
                                                                x="30"
                                                                y="38"
                                                                width="12"
                                                                height="4"
                                                                rx="1"
                                                                fill="white"
                                                            />
                                                            <rect
                                                                x="50"
                                                                y="34"
                                                                width="24"
                                                                height="3"
                                                                rx="1.5"
                                                                fill="#D1D5DB"
                                                            />
                                                            <rect
                                                                x="50"
                                                                y="40"
                                                                width="18"
                                                                height="3"
                                                                rx="1.5"
                                                                fill="#D1D5DB"
                                                            />
                                                            <rect
                                                                x="22"
                                                                y="58"
                                                                width="40"
                                                                height="3"
                                                                rx="1.5"
                                                                fill="#D1D5DB"
                                                            />
                                                            <rect
                                                                x="22"
                                                                y="65"
                                                                width="28"
                                                                height="3"
                                                                rx="1.5"
                                                                fill="#D1D5DB"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <span className={s.emptyTitle}>No secondary insurance</span>
                                                    <span className={s.emptySubtitle}>
                                                        Add one if the patient is covered by more than one plan.
                                                    </span>
                                                    <Button type="primary" onClick={() => addInsurance('SECONDARY')}>
                                                        Add secondary insurance
                                                    </Button>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {!isEdit && !isKlarity && (
                        <>
                            <h3 className={s.h3Btm}>Invitation to patient portal</h3>
                            <div className={s.rowItem}>
                                <Form.Item style={{ width: '80%' }} label="" name={['sendInvitationEmail']}>
                                    <Radio.Group
                                        options={[
                                            {
                                                value: true,
                                                label: 'Send client an invitation to sign up for the patient portal',
                                            },
                                            {
                                                value: false,
                                                label: 'Don’t send client an invitation to sign up for the patient portal',
                                            },
                                        ]}
                                    />
                                </Form.Item>
                            </div>
                        </>
                    )}
                    {!isEdit && (
                        <>
                            <h3 className={s.h3Btm}>Notes</h3>
                            <div className={s.rowItem}>
                                <Form.Item
                                    style={{ width: '80%' }}
                                    label=""
                                    name={['notes']}
                                    validateFirst
                                    rules={[
                                        {
                                            required: false,
                                            message: 'Carrier number is required',
                                        },
                                    ]}
                                >
                                    <InputTextArea rows={5} />
                                </Form.Item>
                            </div>
                        </>
                    )}
                </div>
            </Form>
        </Modal>
    );
};

export default AddAndUpdateProspects;
